"use client"

import React from "react"
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion"
import { Button } from "@/components/ui/button"
import quizData from "../documents.json"

/** 履歴 */
interface AnswerRecord {
  docName: string
  userAnswers: {
    todokedesaki: string
    todokedesha: string
    todokejiki: string
  }
  correctAnswers: {
    todokedesaki: string
    todokedesha: string
    todokejiki: string
  }
  isCorrectObj: {
    todokedesaki: boolean
    todokedesha: boolean
    todokejiki: boolean
  }
  hasNote: boolean
  note: string
}

interface QuizResultProps {
  answerRecords: AnswerRecord[]
  onReset: () => void
}

export function QuizResult({ answerRecords, onReset }: QuizResultProps) {
  // 合計点: (各書類 3点満点)
  let totalScore = 0
  answerRecords.forEach((rec) => {
    const c = rec.isCorrectObj
    if (c.todokedesaki) totalScore++
    if (c.todokedesha) totalScore++
    if (c.todokejiki) totalScore++
  })

  const maxScore = answerRecords.length * 3

  // docName -> type へのマップ
  const docMap: Record<
    string,
    {
      届出先: string
      届出者: string
      届出時期: string
      補足事項: string
      タイプ: string
    }
  > = {}
  quizData.forEach((d) => {
    docMap[d.書類名] = {
      届出先: d.届出先,
      届出者: d.届出者,
      届出時期: d.届出時期,
      補足事項: d.補足事項,
      タイプ: d.タイプ,
    }
  })

  // タイプごとに "書類名" を収集
  const groupByType: Record<string, string[]> = {}
  answerRecords.forEach((rec) => {
    const doc = docMap[rec.docName]
    if (!doc) return
    const typeKey = doc.タイプ
    if (!groupByType[typeKey]) {
      groupByType[typeKey] = []
    }
    groupByType[typeKey].push(rec.docName)
  })

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <h2 className="text-xl font-bold text-center mt-4">クイズ結果</h2>
      <p className="text-center">
        合計得点： <strong>{totalScore}</strong> / {maxScore}
      </p>

      <Accordion type="single" collapsible className="border rounded">
        {/* (1) 出題順 */}
        <AccordionItem value="sequence">
          <AccordionTrigger>回答履歴（出題順）</AccordionTrigger>
          <AccordionContent>
            <div className="overflow-x-auto mt-2">
              <table className="w-full text-sm border border-collapse">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border px-2 py-1">書類名</th>
                    <th className="border px-2 py-1">届出先</th>
                    <th className="border px-2 py-1">届出者</th>
                    <th className="border px-2 py-1">届出時期</th>
                    <th className="border px-2 py-1">補足事項</th>
                  </tr>
                </thead>
                <tbody>
                  {answerRecords.map((rec, idx) => {
                    const c = rec.isCorrectObj
                    const user = rec.userAnswers
                    const cor = rec.correctAnswers

                    const sakiBg = c.todokedesaki ? "bg-green-50" : "bg-red-50"
                    const shaBg = c.todokedesha ? "bg-green-50" : "bg-red-50"
                    const jikiBg = c.todokejiki ? "bg-green-50" : "bg-red-50"

                    return (
                      <tr key={idx}>
                        <td className="border px-2 py-1 align-top font-semibold">
                          {rec.docName}
                        </td>
                        {/* 届出先 */}
                        <td className={`border px-2 py-1 align-top ${sakiBg}`}>
                          {c.todokedesaki ? (
                            // 正解：太字
                            <strong className="text-green-800">
                              {user.todokedesaki}
                            </strong>
                          ) : (
                            // 不正解：薄い文字 + 下に正解
                            <>
                              <p className="text-gray-400">
                                {user.todokedesaki}
                              </p>
                              <p>
                                <strong className="text-red-700">
                                  {cor.todokedesaki}
                                </strong>
                              </p>
                            </>
                          )}
                        </td>
                        {/* 届出者 */}
                        <td className={`border px-2 py-1 align-top ${shaBg}`}>
                          {c.todokedesha ? (
                            <strong className="text-green-800">
                              {user.todokedesha}
                            </strong>
                          ) : (
                            <>
                              <p className="text-gray-400">
                                {user.todokedesha}
                              </p>
                              <p>
                                <strong className="text-red-700">
                                  {cor.todokedesha}
                                </strong>
                              </p>
                            </>
                          )}
                        </td>
                        {/* 届出時期 */}
                        <td className={`border px-2 py-1 align-top ${jikiBg}`}>
                          {c.todokejiki ? (
                            <strong className="text-green-800">
                              {user.todokejiki}
                            </strong>
                          ) : (
                            <>
                              <p className="text-gray-400">{user.todokejiki}</p>
                              <p>
                                <strong className="text-red-700">
                                  {cor.todokejiki}
                                </strong>
                              </p>
                            </>
                          )}
                        </td>
                        <td className="border px-2 py-1 align-top">
                          {rec.hasNote ? rec.note : ""}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* (2) タイプ別一覧 -> 正解・不正解表示なし、documents.jsonの正情報のみ */}
        <AccordionItem value="type-based">
          <AccordionTrigger>タイプ別一覧(正しい情報のみ)</AccordionTrigger>
          <AccordionContent>
            {Object.keys(groupByType).map((typeKey) => {
              const docNames = groupByType[typeKey]
              return (
                <div key={typeKey} className="mt-6">
                  <h3 className="font-bold mb-2 text-lg">タイプ：{typeKey}</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border border-collapse">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="border px-2 py-1">書類名</th>
                          <th className="border px-2 py-1">届出先</th>
                          <th className="border px-2 py-1">届出者</th>
                          <th className="border px-2 py-1">届出時期</th>
                          <th className="border px-2 py-1">補足事項</th>
                        </tr>
                      </thead>
                      <tbody>
                        {docNames.map((name) => {
                          const doc = docMap[name]
                          if (!doc) return null
                          return (
                            <tr key={name}>
                              <td className="border px-2 py-1 align-top">
                                {name}
                              </td>
                              <td className="border px-2 py-1 align-top">
                                {doc.届出先}
                              </td>
                              <td className="border px-2 py-1 align-top">
                                {doc.届出者}
                              </td>
                              <td className="border px-2 py-1 align-top">
                                {doc.届出時期}
                              </td>
                              <td className="border px-2 py-1 align-top">
                                {doc.補足事項 || ""}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            })}
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="text-center mt-4">
        <Button variant="secondary" onClick={onReset}>
          もう一度チャレンジする
        </Button>
      </div>
    </div>
  )
}
